import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutGrid,
  GitBranch,
  Inbox,
  Users,
  Kanban,
  FileText,
  Settings,
  Video,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../store/authStore'

interface NavItem {
  to: string
  icon: React.ReactNode
  label: string
  badge?: number
}

const navItems: NavItem[] = [
  { to: '/', icon: <LayoutGrid className="w-5 h-5" />, label: 'Dashboard' },
  { to: '/pipeline', icon: <GitBranch className="w-5 h-5" />, label: 'Pipeline' },
  { to: '/leads', icon: <Inbox className="w-5 h-5" />, label: 'Заявки' },
  { to: '/meetings', icon: <Video className="w-5 h-5" />, label: 'Встречи' },
  { to: '/clients', icon: <Users className="w-5 h-5" />, label: 'Клиенты' },
  { to: '/projects', icon: <Kanban className="w-5 h-5" />, label: 'Проекты' },
  { to: '/documents', icon: <FileText className="w-5 h-5" />, label: 'Документы' },
]

const bottomNavItems: NavItem[] = [
  { to: '/settings', icon: <Settings className="w-5 h-5" />, label: 'Настройки' },
]

export default function Sidebar() {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 glass-sidebar flex flex-col z-50">
      {/* Logo */}
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="text-xl font-bold tracking-wider text-white">
            11:30 <span className="font-light opacity-50">5TUD10</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
              isActive(item.to)
                ? 'menu-item-active'
                : 'text-text-secondary hover:bg-white/5 hover:text-white'
            )}
          >
            <span className={cn('opacity-60', isActive(item.to) && 'opacity-100')}>
              {item.icon}
            </span>
            <span>{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span
                className={cn(
                  'ml-auto px-2 py-0.5 text-xs font-semibold rounded-full',
                  isActive(item.to)
                    ? 'bg-white/20 text-white'
                    : 'bg-white/10 text-text-secondary'
                )}
              >
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}

        {/* Divider */}
        <div className="h-px bg-white/8 my-4" />

        {/* Bottom navigation items */}
        {bottomNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
              isActive(item.to)
                ? 'menu-item-active'
                : 'text-text-secondary hover:bg-white/5 hover:text-white'
            )}
          >
            <span className={cn('opacity-60', isActive(item.to) && 'opacity-100')}>
              {item.icon}
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User card */}
      <div className="p-3">
        <div className="flex items-center gap-3 p-3 glass-card rounded-lg">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center text-sm font-semibold text-white border border-white/10">
            {user?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">
              {user?.username || 'User'}
            </div>
            <div className="text-xs text-text-secondary capitalize">
              {user?.role || 'Admin'}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
